/**
 * Event Log Table Column Definitions
 * 
 * TanStack Table column definitions for Event Logs table
 * Includes custom cell renderers for status badges, dates, and details
 */

import { ColumnDef } from '@tanstack/react-table';
import { FiEye, FiClock, FiAlertCircle } from 'react-icons/fi';
import Button from '@/components/Common/Button';
import type { EventLogListItem } from '@/types';
import styles from './eventLogColumns.module.css';

/**
 * Format date to readable string
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Format relative time (e.g., "2 hours ago")
 */
const formatRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / 60000);
  
  if (diffInMinutes < 1) return 'Agora';
  if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h atrás`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays}d atrás`;
  
  return formatDate(dateString);
};

/**
 * Get status badge variant based on status string
 */
const getStatusVariant = (status: string): 'success' | 'warning' | 'error' | 'info' => {
  const upperStatus = status.toUpperCase();
  
  if (upperStatus === 'SUCCESS') return 'success';
  if (upperStatus.includes('ERROR') || upperStatus === 'FAILED') return 'error';
  if (upperStatus === 'PENDING' || upperStatus === 'RETRY') return 'warning';
  
  return 'info';
};

/**
 * Event Log Actions Props
 */
export interface EventLogActionsProps {
  onView?: (eventLog: EventLogListItem) => void;
}

/**
 * Create event log columns for TanStack Table
 */
export const createEventLogColumns = (actions: EventLogActionsProps): ColumnDef<EventLogListItem>[] => [
  {
    id: 'id',
    accessorKey: 'id',
    header: 'ID',
    cell: ({ row }) => (
      <span className={styles.idCell}>#{row.original.id}</span>
    ),
    enableSorting: true,
    size: 80,
  },
  {
    id: 'agentEventId',
    accessorKey: 'agentEventId',
    header: 'Event ID',
    cell: ({ row }) => (
      <div className={styles.eventIdCell}>
        <span className={styles.eventId}>#{row.original.agentEventId}</span>
        {row.original.isRecent && (
          <span className={styles.recentBadge} title="Evento recente">
            <FiClock size={12} />
          </span>
        )}
      </div>
    ),
    enableSorting: true,
    size: 120,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const status = row.original.status;
      const variant = getStatusVariant(status);
      
      return (
        <div className={styles.statusCell}>
          <span className={`${styles.statusBadge} ${styles[variant]}`}>
            {status}
          </span>
        </div>
      );
    },
    enableSorting: true,
    size: 120,
  },
  {
    id: 'retries',
    accessorKey: 'retries',
    header: 'Tentativas',
    cell: ({ row }) => {
      const retries = row.original.retries;
      
      return (
        <div className={styles.retriesCell}>
          <span className={`${styles.retriesCount} ${retries > 0 ? styles.hasRetries : ''}`}>
            {retries}
          </span>
          {retries > 3 && (
            <FiAlertCircle 
              size={14} 
              className={styles.retriesWarning}
              title="Muitas tentativas"
            />
          )}
        </div>
      );
    },
    enableSorting: true,
    size: 100,
  },
  {
    id: 'errorMessage',
    accessorKey: 'errorMessage',
    header: 'Erro',
    cell: ({ row }) => {
      const errorMessage = row.original.errorMessage;
      
      if (!errorMessage) {
        return <span className={styles.noError}>-</span>;
      }
      
      return (
        <div className={styles.errorCell}>
          <span 
            className={styles.errorMessage}
            title={errorMessage}
          >
            {errorMessage.length > 50 
              ? `${errorMessage.substring(0, 50)}...` 
              : errorMessage
            }
          </span>
        </div>
      );
    },
    enableSorting: false,
    size: 200,
  },
  {
    id: 'datProcess',
    accessorKey: 'datProcess',
    header: 'Processado em',
    cell: ({ row }) => {
      const datProcess = row.original.datProcess;
      
      return (
        <span 
          className={styles.dateCell} 
          title={formatDate(datProcess)}
        >
          {formatRelativeTime(datProcess)}
        </span>
      );
    },
    enableSorting: true,
    size: 140,
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Criado em',
    cell: ({ row }) => (
      <span className={styles.dateCell}>
        {formatDate(row.original.createdAt)}
      </span>
    ),
    enableSorting: true,
    size: 140,
  },
  {
    id: 'actions',
    header: 'Ações',
    cell: ({ row }) => {
      const eventLog = row.original;
      
      return (
        <div className={styles.actionsWrapper}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => actions.onView?.(eventLog)}
            title="Ver detalhes"
            iconLeft={<FiEye size={16} />}
          />
        </div>
      );
    },
    enableSorting: false,
    size: 80,
  },
];